import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { PRO_TIER_NAME } from '../lib/productTier';
import { isNativeIOS } from '../lib/platform';
import { manageAppleSubscriptions } from '../lib/appleIap';
import {
  checkoutErrText,
  purchaseSubscriptionPlan,
  restoreSubscriptionPurchases,
} from '../lib/subscriptionCheckout';
import { btnGhost, btnPrimary } from '../lib/uiClasses';
import { SubscriptionLegalLinks } from './SubscriptionLegalLinks';
import { AppleOfferCodeField } from './AppleOfferCodeField';
import {
  fallbackSubscriptionOffer,
  loadSubscriptionOffer,
  type SubscriptionOfferDisplay,
} from '../lib/subscriptionOffer';

type Props = {
  title?: string;
  /** When set, shows a close control and allows tapping the backdrop to dismiss. */
  onClose?: () => void;
};

export function StripeUnlockModal({ title = PRO_TIER_NAME, onClose }: Props) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [offer, setOffer] = useState<SubscriptionOfferDisplay>(() => fallbackSubscriptionOffer('coach'));
  const [promoFirstMonth, setPromoFirstMonth] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadSubscriptionOffer('coach').then((o) => {
      if (!cancelled) setOffer(o);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function startCheckout() {
    setErr(null);
    setBusy(true);
    try {
      await purchaseSubscriptionPlan('coach', refresh);
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    setErr(null);
    setBusy(true);
    try {
      const ok = await restoreSubscriptionPurchases(refresh);
      if (!ok) setErr('No active subscription found for this Apple ID.');
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stripe-unlock-title"
      onClick={onClose ? () => onClose() : undefined}
    >
      <div
        className="glass-panel relative max-h-[min(90dvh,640px)] w-full max-w-md overflow-y-auto rounded-2xl p-6 pt-8 shadow-card sm:pt-6"
        onClick={(e) => e.stopPropagation()}
      >
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className={`absolute right-2 top-2 ${btnGhost} !h-9 !w-9 !p-0`}
            aria-label="Close dialog"
          >
            <span className="text-xl leading-none" aria-hidden>
              ×
            </span>
          </button>
        ) : null}

        <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Auto-renewable subscription</p>
        <h2 id="stripe-unlock-title" className="pr-8 text-lg font-semibold text-zinc-100">
          {title}
        </h2>
        <p className="mt-1 text-xs text-zinc-500">{offer.title} · {offer.periodLabel}</p>

        {/* Billed amount — most clear and conspicuous pricing element (Guideline 3.1.2c) */}
        <div className="mt-5 rounded-2xl border border-sky-400/25 bg-sky-500/10 px-4 py-4 text-center">
          <p className="text-[11px] font-medium uppercase tracking-wide text-sky-200/80">
            {promoFirstMonth ? 'First month with code' : 'Billed amount'}
          </p>
          <p className="mt-1 font-display text-4xl font-bold tabular-nums tracking-tight text-white">
            {promoFirstMonth ?? offer.priceLabel}
          </p>
          <p className="mt-1 text-sm font-semibold text-sky-100/90">
            {promoFirstMonth
              ? `then ${offer.pricePerPeriodLabel}`
              : `per ${offer.periodLabel.replace(/^1 /, '')}`}
          </p>
          <p className="mt-2 text-xs text-zinc-400">
            {promoFirstMonth ? 'Confirm the code on Apple’s sheet to unlock this price.' : offer.pricePerPeriodLabel}
          </p>
        </div>

        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Included each subscription period</p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-zinc-400">
            {offer.services.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>

        {isNativeIOS() ? (
          <p className="mt-4 text-[11px] leading-relaxed text-zinc-500">{offer.renewNote}</p>
        ) : (
          <p className="mt-4 text-[11px] leading-relaxed text-zinc-500">
            Billed through Stripe on the web. Cancel anytime from your account billing tools.
          </p>
        )}

        {isNativeIOS() ? (
          <AppleOfferCodeField
            plan="coach"
            disabled={busy}
            onApplied={({ firstMonthPriceLabel }) => setPromoFirstMonth(firstMonthPriceLabel)}
            onUnlocked={() => void refresh()}
          />
        ) : null}

        {err ? (
          <p className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{err}</p>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={() => void startCheckout()}
          className={`${btnPrimary} mt-5 w-full shadow-lg shadow-black/20`}
        >
          {busy
            ? 'Processing…'
            : isNativeIOS()
              ? `Subscribe · ${offer.pricePerPeriodLabel}`
              : `Unlock ${PRO_TIER_NAME} · ${offer.pricePerPeriodLabel}`}
        </button>

        {isNativeIOS() ? (
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void restore()}
              className={`${btnGhost} w-full text-sm`}
            >
              Restore purchases
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void manageAppleSubscriptions()}
              className={`${btnGhost} w-full text-sm`}
            >
              Manage subscription
            </button>
          </div>
        ) : null}
        <SubscriptionLegalLinks className="mt-5" />
      </div>
    </div>
  );
}
